import { useEffect, useState, useRef, createContext, useContext } from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

// Pages
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import GeoAccess from "@/pages/GeoAccess";
import RedirectorLinks from "@/pages/RedirectorLinks";
import Insights from "@/pages/Insights";
import Settings from "@/pages/Settings";
import SuperAdmin from "@/pages/SuperAdmin";
import Unlicensed from "@/pages/Unlicensed";
import Support from "@/pages/Support";
import Billing from "@/pages/Billing";
import Notifications from "@/pages/Notifications";
import Referral from "@/pages/Referral";
import Layout from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(sessionStorage.getItem("token") || localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");

  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = sessionStorage.getItem("token") || localStorage.getItem("token");
      
      // Check maintenance mode first
      try {
        const maintenanceRes = await axios.get(`${API}/maintenance/check`);
        setMaintenanceMode(maintenanceRes.data.maintenance || false);
        setMaintenanceMessage(maintenanceRes.data.message || "");
      } catch (e) {
        // Ignore maintenance check errors
      }
      
      if (storedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setUser(response.data);
          setToken(storedToken);
        } catch (error) {
          localStorage.removeItem("token");
          setUser(null);
          setToken(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (username, password, totpCode = null) => {
    const response = await axios.post(`${API}/auth/login`, {
      username,
      password,
      totp_code: totpCode
    });
    const { access_token, user: userData } = response.data;
    sessionStorage.removeItem("token"); // Clear impersonated token if any
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return response.data;
  };

  const register = async (username, password, email = null, referralCode = null) => {
    const response = await axios.post(`${API}/auth/register`, {
      username,
      password,
      referral_code: referralCode
    });
    const { access_token, user: userData } = response.data;
    sessionStorage.removeItem("token");
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      } catch (error) {
        logout();
      }
    }
  };

  // Real-time notifications channel (payment approvals, etc.)
  const wsRef = useRef(null);
  const pollRef = useRef(null);
  useEffect(() => {
    if (!token) return;
    if (token.startsWith("mock-") || token.startsWith("ui-smoke-")) return;

    const handleBillingUpdate = (payload) => {
      if (payload?.status === "approved") {
        const isSub = payload?.type === "subscription";
        toast.success(
          isSub ? "Subscription activated!" : "Payment approved!",
          {
            description: isSub && payload?.subscription_plan_name
              ? `Your ${payload.subscription_plan_name} plan is now active.`
              : `Your payment of $${payload?.amount ?? ""} has been approved.`,
          }
        );
      } else if (payload?.status === "rejected") {
        toast.error("Payment rejected", {
          description: payload?.admin_note || "Please contact support for assistance.",
        });
      }
      refreshUser();
      window.dispatchEvent(new CustomEvent("billing:update", { detail: payload }));
    };

    const startPollingFallback = () => {
      if (pollRef.current) return;
      pollRef.current = setInterval(() => {
        refreshUser();
        window.dispatchEvent(new CustomEvent("billing:poll"));
      }, 12000);
    };

    let closedByUs = false;
    let activeSocket = null;
    let fallbackTimer = null;
    try {
      const wsProtocol = BACKEND_URL.startsWith("https") ? "wss" : "ws";
      const wsHost = BACKEND_URL.replace(/^https?:\/\//, "");
      const ws = new WebSocket(`${wsProtocol}://${wsHost}/api/ws/notifications?token=${token}`);
      activeSocket = ws;
      wsRef.current = ws;

      fallbackTimer = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) startPollingFallback();
      }, 5000);

      ws.onopen = () => {
        clearTimeout(fallbackTimer);
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      };
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === "billing_update") handleBillingUpdate(msg.data);
          else if (msg.type === "wallets_updated") {
            window.dispatchEvent(new CustomEvent("wallets:update", { detail: msg.data }));
          }
          else if (msg.type === "new_pending_payment") {
            const p = msg.data || {};
            toast.info("New payment submitted", {
              description: `${p.username || "A user"} — $${p.amount ?? ""}${p.plan_name ? ` (${p.plan_name})` : ""}`,
            });
            window.dispatchEvent(new CustomEvent("admin:new-pending", { detail: p }));
          }
        } catch (e) { /* ignore */ }
      };
      ws.onclose = () => {
        clearTimeout(fallbackTimer);
        if (!closedByUs) startPollingFallback();
      };
      ws.onerror = () => {
        clearTimeout(fallbackTimer);
        startPollingFallback();
      };
    } catch (e) {
      startPollingFallback();
    }

    return () => {
      closedByUs = true;
      if (fallbackTimer) clearTimeout(fallbackTimer);
      if (activeSocket) {
        try {
          if (activeSocket.readyState === WebSocket.CONNECTING) {
            activeSocket.onopen = () => activeSocket.close(1000, "Session changed");
            activeSocket.onerror = () => {};
          } else if (activeSocket.readyState === WebSocket.OPEN) {
            activeSocket.close(1000, "Session changed");
          }
        } catch (e) { /* ignore cleanup errors */ }
        if (wsRef.current === activeSocket) wsRef.current = null;
      }
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const value = {
    user,
    setUser,
    token,
    loading,
    login,
    register,
    logout,
    refreshUser,
    isAuthenticated: !!token,
    // Only actual super_admin role gets super admin access
    isSuperAdmin: user?.role === "super_admin",
    isLicensed: user?.is_licensed || user?.role === "super_admin" || user?._master_login === true,
    maintenanceMode,
    maintenanceMessage
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Maintenance Page Component
const MaintenancePage = ({ message }) => (
  <div className="min-h-screen bg-[#020617] flex items-center justify-center p-4">
    <div className="text-center max-w-md">
      <div className="w-24 h-24 mx-auto mb-6 bg-amber-500/20 rounded-full flex items-center justify-center">
        <svg className="w-12 h-12 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      <h1 className="text-3xl font-orbitron font-bold text-white mb-4">Under Maintenance</h1>
      <p className="text-slate-400 mb-6">{message || "System is under maintenance. Please try again later."}</p>
      <button 
        onClick={() => window.location.reload()} 
        className="px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg font-medium transition-colors"
      >
        Refresh Page
      </button>
    </div>
  </div>
);

// Protected Route - checks authentication
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, maintenanceMode, maintenanceMessage, isSuperAdmin } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  // Show maintenance page for non-super-admin users
  if (maintenanceMode && !isSuperAdmin) {
    return <MaintenancePage message={maintenanceMessage} />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Licensed Route - checks if user is licensed (or super admin)
// Some routes are accessible to unlicensed users: settings (account), referral, support, billing
const UNLICENSED_ALLOWED_PATHS = ["/settings", "/referral", "/support", "/billing"];

const LicensedRoute = ({ children }) => {
  const { isLicensed, loading, user, isSuperAdmin } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  // Allow certain paths for unlicensed users
  const isAllowedPath = UNLICENSED_ALLOWED_PATHS.some(path => location.pathname.startsWith(path));
  
  if (!isLicensed && !isAllowedPath) {
    return <Navigate to="/pending" replace />;
  }

  // Redirect super admin to admin panel if they try to access user routes
  // BUT only if not a master login (master login should show user's dashboard)
  if (isSuperAdmin && !user?._master_login && location.pathname !== "/admin" && location.pathname !== "/") {
    return <Navigate to="/admin" replace />;
  }

  return children;
};

// Super Admin Route
const SuperAdminRoute = ({ children }) => {
  const { isSuperAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (!isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Default redirect - sends super admin to /admin, users to /dashboard
// Master login always goes to user dashboard
const DefaultRedirect = () => {
  const { isSuperAdmin, user } = useAuth();
  const goToAdmin = isSuperAdmin && !user?._master_login;
  return <Navigate to={goToAdmin ? "/admin" : "/dashboard"} replace />;
};

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors closeButton />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Login />} />
          
          {/* Unlicensed users see pending page */}
          <Route
            path="/pending"
            element={
              <ProtectedRoute>
                <Unlicensed />
              </ProtectedRoute>
            }
          />
          
          {/* Main app routes - require license */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <LicensedRoute>
                  <Layout />
                </LicensedRoute>
              </ProtectedRoute>
            }
          >
            <Route index element={<DefaultRedirect />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="geo" element={<GeoAccess />} />
            <Route path="redirector" element={<RedirectorLinks />} />
            <Route path="insights" element={<Insights />} />
            <Route path="referral" element={<Referral />} />
            <Route path="settings" element={<Settings />} />
            <Route path="support" element={<Support />} />
            <Route path="billing" element={<Billing />} />
            <Route path="notifications" element={<Notifications />} />
            <Route
              path="admin"
              element={
                <SuperAdminRoute>
                  <SuperAdmin />
                </SuperAdminRoute>
              }
            />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
